import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';

const NETWORK_ERROR_MESSAGE =
  'Die Netzwerkverbindung konnte nicht hergestellt werden. Bitte überprüfe deine Verbindung und versuche es erneut.';
const VALIDATION_ERROR_MESSAGE =
  'Deine Eingaben konnten nicht verarbeitet werden. Bitte überprüfe sie und versuche es erneut.';
const UNEXPECTED_ERROR_MESSAGE =
  'Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.';
const REPOSITORY_CONSISTENCY_ERROR_PREFIX = 'Repository consistency validation failed';
const REPOSITORY_CONSISTENCY_ERROR_MESSAGE =
  'Die Repository-Dokumentation ist inkonsistent. Bitte korrigiere die fehlerhaften Verweise und versuche es erneut.';
const STATUS_MESSAGES: Readonly<Record<number, string>> = {
  0: NETWORK_ERROR_MESSAGE,
  400: VALIDATION_ERROR_MESSAGE,
  422: VALIDATION_ERROR_MESSAGE,
  401: 'Deine Sitzung ist nicht mehr gültig. Bitte melde dich erneut an.',
  403: 'Du hast keine Berechtigung für diese Aktion.',
};
const NOT_FOUND_RESOURCE_MESSAGES: ReadonlyArray<readonly [RegExp, string]> = [
  [/\/concepts\/[^/]+$/, 'Das Konzept wurde nicht gefunden.'],
  [/\/groups\/[^/]+$/, 'Die Gruppe wurde nicht gefunden.'],
  [/\/adrs\/[^/]+(?:\/summary)?$/, 'Der ADR wurde nicht gefunden.'],
  [/\/architecture\/arc42\/[^/]+$/, 'Das Architekturkapitel wurde nicht gefunden.'],
  [/\/api-specifications\/detail(?:\?|$)/, 'Die API-Spezifikation wurde nicht gefunden.'],
];

function statusMessageFor(error: HttpErrorResponse): string | undefined {
  if (
    error.status === 500 &&
    typeof error.error?.message === 'string' &&
    error.error.message.startsWith(REPOSITORY_CONSISTENCY_ERROR_PREFIX)
  ) {
    return REPOSITORY_CONSISTENCY_ERROR_MESSAGE;
  }
  if (error.status === 404) {
    const match = NOT_FOUND_RESOURCE_MESSAGES.find(([pattern]) => pattern.test(error.url ?? ''));
    return match?.[1] ?? 'Die angeforderten Daten wurden nicht gefunden.';
  }
  return STATUS_MESSAGES[error.status];
}

/** Maps technical failures to messages that are safe to display to users. */
export function errorMessageFor(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) {
    return UNEXPECTED_ERROR_MESSAGE;
  }

  const statusMessage = statusMessageFor(error);
  if (statusMessage) {
    return statusMessage;
  }
  if (error.status >= 500) {
    return 'Der Server konnte die Anfrage nicht verarbeiten. Bitte versuche es später erneut.';
  }
  return UNEXPECTED_ERROR_MESSAGE;
}

/** Holds the currently displayed global error toast. */
@Injectable({ providedIn: 'root' })
export class ErrorNotificationService {
  readonly message = signal<string | null>(null);

  showError(message: string): void {
    this.message.set(message);
  }

  dismiss(): void {
    this.message.set(null);
  }
}
